/**
 * Rooms this browser created, remembered on the spot.
 *
 * The record of who owns a room is a write to the backend, and reading it back
 * is a second round trip that can lose the race with the creator walking in —
 * or fail entirely on a backend where the note could not be written. Either way
 * the person who just created the room would not be its host, which is never
 * the right answer. So creating also remembers it here, and the claim is made
 * from local knowledge while the record is still catching up.
 *
 * Session-scoped: a claim must not outlive the tab that made it.
 */

const STORAGE_KEY = "syncani:owned-rooms";

const normalise = (code: string) => code.trim().toLowerCase();

// Storage is reached through `globalThis` and a catch rather than a window
// check: on the server there is no storage at all, and a refusing browser
// behaves the same way — both mean "nothing is remembered here".
const read = (): string[] => {
  try {
    const raw = globalThis.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === "string")
      : [];
  } catch {
    return [];
  }
};

/** Notes that this browser created the room, so it hosts it. */
export function rememberOwnedRoom(code: string): void {
  const trimmed = normalise(code);
  if (!trimmed) return;
  const next = Array.from(new Set([...read(), trimmed]));
  try {
    globalThis.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A browser that refuses storage simply falls back to the record.
  }
}

/** Takes back one claim: the code turned out to be somebody else's room. */
export function forgetOwnedRoom(code: string): void {
  const trimmed = normalise(code);
  try {
    globalThis.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(read().filter((owned) => owned !== trimmed)),
    );
  } catch {
    // Nothing was remembered anyway.
  }
}

/** Whether this browser created the room. */
export function ownsRoomLocally(code: string): boolean {
  const trimmed = normalise(code);
  if (!trimmed) return false;
  return read().includes(trimmed);
}

/** Drops every claim this browser made — signing out ends them all. */
export function forgetOwnedRooms(): void {
  try {
    globalThis.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing was remembered anyway.
  }
}
